import { ButtonHTMLAttributes, useState } from 'react';
import { Check, Search } from 'lucide-react';
import { Button } from '@/components/ui-kit/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui-kit/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui-kit/popover';
import { cn } from '@/lib/utils';
import { InlineSkeleton, OptionRowsSkeleton } from '../skeletons/skeletons';

interface SearchableSelectOption {
  value: string;
  label: string;
  /** Secondary text shown after the label (e.g. an e-mail address). */
  description?: string;
}

export interface SearchableSelectProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'onChange' | 'value'
> {
  value: string;
  options: SearchableSelectOption[];
  onChange: (value: string, option: SearchableSelectOption) => void;
  placeholder: string;
  searchPlaceholder: string;
  emptyText: string;
  /** Label to show while `value` is not (yet) among `options`, e.g. a saved manager outside the current search results. */
  selectedLabel?: string;
  /** Provide to search on the server: the options are then shown as-is instead of being filtered locally. */
  onSearchChange?: (keyword: string) => void;
  /** The options are still loading: the list shows skeleton rows instead of `emptyText`, and a picked
   * value whose label isn't known yet shows a skeleton in the trigger. */
  isLoading?: boolean;
}

/** Popover + command list select with a search box (legacy `mat-select-search` / autocomplete). */
export const SearchableSelect = ({
  value,
  options,
  onChange,
  placeholder,
  searchPlaceholder,
  emptyText,
  selectedLabel,
  onSearchChange,
  isLoading = false,
  disabled,
  className,
  ...triggerProps
}: SearchableSelectProps) => {
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState('');

  const selected = options.find((option) => option.value === value);
  const triggerLabel = selected?.label ?? (value ? selectedLabel : undefined);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      setKeyword('');
      onSearchChange?.('');
    }
  };

  const handleKeywordChange = (next: string) => {
    setKeyword(next);
    onSearchChange?.(next);
  };

  return (
    // modal: keeps the list scrollable when this select is used inside a Dialog.
    <Popover open={open} onOpenChange={handleOpenChange} modal>
      <PopoverTrigger asChild>
        <Button
          {...triggerProps}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'h-9 w-full justify-between px-3 font-normal',
            !triggerLabel && 'text-muted-foreground',
            className
          )}
        >
          {isLoading && value && !triggerLabel ? (
            <InlineSkeleton className="w-32" />
          ) : (
            <span className="truncate">{triggerLabel || placeholder}</span>
          )}
          <Search className="size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] p-0">
        <Command shouldFilter={!onSearchChange}>
          <CommandInput
            value={keyword}
            onValueChange={handleKeywordChange}
            placeholder={searchPlaceholder}
          />
          <CommandList>
            {isLoading && options.length === 0 ? (
              <OptionRowsSkeleton />
            ) : (
              <CommandEmpty>{emptyText}</CommandEmpty>
            )}
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={`${option.label} ${option.description ?? ''}`.trim()}
                  onSelect={() => {
                    onChange(option.value, option);
                    handleOpenChange(false);
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 size-4',
                      option.value === value ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate">{option.label}</span>
                    {option.description && (
                      <span className="truncate text-xs text-muted-foreground">
                        {option.description}
                      </span>
                    )}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
